import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { MailCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { GatewayBrand, GatewayContent, GatewayShell } from '@/_shared/components/GatewayShell';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { RequireNativeSignIn } from '../components/nativeSignIn/RequireNativeSignIn';
import { TextLink } from '../components/nativeSignIn/TextLink';
import { emailSchema, type EmailValues } from '../lib/authFormSchema';
import { sendPasswordResetEmail } from '../lib/rudderstack-auth-client';

/** `/forgot-password`: asks config-backend to email a reset link. */
export function ForgotPassword(): React.ReactElement {
  usePageTitle('Reset password');
  return <RequireNativeSignIn>{(config) => <ForgotPasswordForm apiBaseUrl={config.apiBaseUrl} />}</RequireNativeSignIn>;
}

function ForgotPasswordForm({ apiBaseUrl }: { apiBaseUrl: string }): React.ReactElement {
  const form = useForm<EmailValues>({
    resolver: zodResolver(emailSchema),
    mode: 'onTouched',
    defaultValues: { email: '' },
  });
  const send = useMutation({ mutationFn: (email: string) => sendPasswordResetEmail(apiBaseUrl, email) });

  if (send.isSuccess) {
    return (
      <GatewayShell>
        <GatewayBrand poweredBy />
        {/* The same answer for every email, so the page never tells whether an account exists. */}
        <GatewayContent
          icon={<MailCheck />}
          variant="success"
          title="Check your email"
          description="If an account exists for this email, we sent a link to reset your password."
        >
          <TextLink to="/login">Back to sign in</TextLink>
        </GatewayContent>
      </GatewayShell>
    );
  }

  return (
    <GatewayShell>
      <GatewayBrand poweredBy />
      <GatewayContent
        title="Reset your password"
        description="Enter your email and we will send you a link to set a new password."
      >
        <Form {...form}>
          <form
            noValidate
            aria-label="Reset your password"
            className="flex flex-col gap-4 text-left"
            onSubmit={form.handleSubmit((values) => send.mutate(values.email))}
          >
            {send.error && (
              <Callout variant="error" role="alert">
                {send.error.message}
              </Callout>
            )}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" size="lg" autoComplete="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" variant="primary" size="lg" className="w-full" loading={send.isPending}>
              Send reset link
            </Button>
          </form>
        </Form>
        <p className="mt-4 text-xs">
          <TextLink to="/login" tone="quiet">
            Back to sign in
          </TextLink>
        </p>
      </GatewayContent>
    </GatewayShell>
  );
}
